import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_NUMBER_PREFIXES, formatObjectNumber, NUMBER_OBJECTS, NUMBER_PREFIX_MAX, type NumberObjectKey, type NumberPrefixSettings } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';

type Draft = Record<NumberObjectKey, string>;

/**
 * Einrichtung → Nummernkennungen (Änderungswunsch vom 19.10.2026): Für alles, was eine laufende Nummer bekommt, lässt sich die Kennung vorn ändern, zum Beispiel „UNIQ“ statt „RE“.
 * Jahr und Serie dahinter bleiben, die Serie läuft mit der neuen Kennung weiter. Eine Änderung gilt nur für neue Nummern, vergebene bleiben, wie sie sind.
 */
export function NumberPrefixesPage() {
  const { can } = useSession();
  const canWrite = can('setup.write');
  const client = useQueryClient();
  const settings = useQuery({ queryKey: ['number-prefixes'], queryFn: async () => (await api<{ data: NumberPrefixSettings }>('GET', '/number-prefixes')).data });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (settings.data && !draft) setDraft(Object.fromEntries(NUMBER_OBJECTS.map((o) => [o.key, settings.data!.custom[o.key] ?? ''])) as Draft);
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: async () => (await api<{ data: NumberPrefixSettings }>('PUT', '/number-prefixes', Object.fromEntries(NUMBER_OBJECTS.map((o) => [o.key, (draft?.[o.key] ?? '').trim() || null])))).data,
    onSuccess: (r) => {
      client.setQueryData(['number-prefixes'], r);
      client.invalidateQueries({ queryKey: ['numbering'] });
      client.invalidateQueries({ queryKey: ['numbering-preview'] });
      setDraft(Object.fromEntries(NUMBER_OBJECTS.map((o) => [o.key, r.custom[o.key] ?? ''])) as Draft);
      setErrors({});
      setNotice({ ok: true, text: 'Gespeichert. Die neuen Kennungen gelten ab der nächsten vergebenen Nummer.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setNotice(null);
      } else setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Das Speichern hat nicht geklappt.' });
    },
  });

  if (settings.isLoading || !draft) return <p className="muted">Wird geladen …</p>;
  if (!settings.data) return <div className="alert alert--error" role="alert">Die Kennungen konnten nicht geladen werden.</div>;
  const saved = settings.data;
  const changed = NUMBER_OBJECTS.some((o) => (draft[o.key] ?? '').trim().toUpperCase() !== (saved.custom[o.key] ?? ''));
  const year = new Date().getFullYear();
  const groups = [...new Set(NUMBER_OBJECTS.map((o) => o.group))];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setErrors({});
    save.mutate();
  };

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Nummernkennungen</h2>
      <p className="muted small">
        Hier bestimmen Sie, womit die Nummern in Ihrem Unternehmen beginnen, zum Beispiel „UNIQ“ statt „RE“ bei Rechnungen. Das gilt für alles, was eine laufende Nummer bekommt. Jahr und Nummernserie dahinter
        bleiben, und die Serie läuft mit der neuen Kennung weiter. Eine Änderung gilt nur für neue Nummern, denn vergebene Nummern ändern sich nie, bei ausgestellten Rechnungen schreibt das die GoBD vor. Wie der
        Rest der Rechnungsnummer aufgebaut ist, stellen Sie unter <Link to="/einrichtung/rechnungsnummern">Rechnungsnummern</Link> ein.
      </p>
      {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}

      {groups.map((group) => (
        <fieldset key={group} className="field">
          <legend><strong>{group}</strong></legend>
          <div className="form-grid">
            {NUMBER_OBJECTS.filter((o) => o.group === group).map((o) => {
              const value = draft[o.key] ?? '';
              const shown = value.trim().toUpperCase();
              const example = formatObjectNumber(o.key, shown, { year, sequence: o.key === 'employee' ? 1001 : 1 });
              return (
                <label key={o.key} className="field">
                  <span>{o.label}</span>
                  <input
                    className="input"
                    value={value}
                    maxLength={NUMBER_PREFIX_MAX}
                    placeholder={DEFAULT_NUMBER_PREFIXES[o.key] || 'keine'}
                    disabled={!canWrite}
                    autoCapitalize="characters"
                    autoComplete="off"
                    aria-invalid={!!errors[o.key]}
                    onChange={(e) => setDraft({ ...draft, [o.key]: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                  />
                  {errors[o.key] && <span className="field__error" role="alert">{errors[o.key]}</span>}
                  <span className="muted small">
                    Beispiel: <strong>{example}</strong>
                    {o.key === 'employee' ? ' (ohne Kennung nur die Zahl)' : o.key === 'invoice_out' || o.key === 'credit_note' || o.key === 'cancellation' ? ' (je nach Schema der Rechnungsnummer)' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}

      <p className="muted small">
        Erlaubt sind Buchstaben und Ziffern, höchstens {NUMBER_PREFIX_MAX} Zeichen. Rechnungen, Gutschriften und Stornorechnungen brauchen verschiedene Kennungen. Leer lassen heißt: Standardkennung. Debitoren- und
        Kreditorennummern sind Konten im Kontenrahmen und bleiben reine Zahlen.
      </p>

      {canWrite && (
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending || !changed}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
          <button className="btn btn--secondary" type="button" disabled={save.isPending} onClick={() => setDraft(Object.fromEntries(NUMBER_OBJECTS.map((o) => [o.key, ''])) as Draft)}>Alle auf Standard setzen</button>
        </div>
      )}
    </form>
  );
}
